import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import type { HomePublicConfig } from "../../../api/home-config";
import { MediaImage } from "../../../components/MediaImage";
import { usePinnedTrack } from "../../../hooks/usePinnedTrack";
import { useScrollEnter } from "../../../hooks/useScrollEnter";
import { HomeCharacterScene } from "./HomeCharacterScene";
import { HomeSkip } from "./HomeSkip";

type HomeMaterialCharactersProps = { config: HomePublicConfig["characters"] };

export function HomeMaterialCharacters({ config }: HomeMaterialCharactersProps) {
  const track = usePinnedTrack<HTMLElement, HTMLDivElement>(config.items.length, 1.6);
  const stageRef = useScrollEnter<HTMLDivElement>(0);

  return (
    <section
      ref={track.sectionRef}
      id="home-characters"
      className={`home-characters${track.pinned ? " is-pinned" : ""}`}
      aria-labelledby="home-characters-title"
    >
      <div ref={stageRef} className="home-characters-stage">
        <div ref={track.trackRef} className="home-characters-track">
          <header className="home-characters-intro">
            <h2 id="home-characters-title">
              <span>{config.title}</span>
              <span>{config.subtitle}</span>
            </h2>
            <p>{config.description}</p>
          </header>
          {config.items.map((item) => (
            <article className="home-character" key={item.id} aria-labelledby={`home-character-${item.id}`}>
              <div className="home-character-visual">
                <div
                  className={`home-character-slab is-${item.shape}${item.imageUrl ? " has-image" : item.builtinSurface ? ` m-${item.builtinSurface}` : " is-unavailable"}`}
                  role="img"
                  aria-label={`${item.name}材质样本`}
                >
                  {item.imageUrl ? (
                    <MediaImage key={item.imageUrl} src={item.imageUrl} alt={`${item.name}材质样本`} />
                  ) : !item.builtinSurface ? (
                    <span>样本图片暂不可用</span>
                  ) : null}
                </div>
                <HomeCharacterScene materialName={item.name} application={item.application} />
              </div>
              <div className="home-character-copy">
                {item.english && (
                  <span className="home-character-en" lang="en">
                    {item.english}
                  </span>
                )}
                <h3 id={`home-character-${item.id}`}>{item.name}</h3>
                <p>{item.description}</p>
                <ul aria-label="材质标签">
                  {item.tags.filter(Boolean).map((tag, index) => (
                    <li key={`${tag}-${index}`}>{tag}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
          <Link className="home-characters-more" to="/library?kind=material">
            <span>
              在材质库里
              <br />
              看全部样本
            </span>
            <ArrowUpRight size={34} strokeWidth={1.4} />
          </Link>
        </div>
        <HomeSkip targetId="featured" label="跳过，看社区精选" className="is-characters" visible={track.pinned} />
      </div>
    </section>
  );
}
